import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  deleteGiftCouponSettlement,
  getGiftCouponSettlementDetail,
  getGiftCouponSettlementSummary,
  GiftCouponSettlementRole,
  patchGiftCouponSettlement,
  patchGiftCouponSettlementLines,
  recomputeGiftCouponSettlement,
} from '@shared/api/couponSettlement/couponSettlement.controller';
import {
  CouponSettlementLineRow,
  GiftCouponSettlementDetailResponse,
  GiftCouponSettlementSummaryItem,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { GiftSettlementStatus } from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import { useToast } from '../../hooks/useToast';
import {
  amountColorClass,
  amountLabel,
  couponCodeLabel,
  describeSkipped,
  formatSettlementAmount,
} from '../../utils/coupon-settlement.utils';
import { exportCouponSettlementDetailExcel } from '../../utils/couponSettlementExcel';
import { formatDate } from '../../utils/delivery.utils';
import { statusColor, statusLabel, toYmd } from '../../utils/rebate-settlement.utils';
import { floorWon } from '../../utils/settlement.utils';
import { CouponHistoryDetailModal } from '../agencyGiftCouponSettlement/CouponHistoryDetailModal';
import { Toast } from '../Toast';
import { CouponAddPendingModal } from './CouponAddPendingModal';

// ─────────────────────────────────────────────
// CouponHistorySection
//
// 쿠폰형 정산의 "정산 완료 내역". 단계별 로드: 목록은 lines 없는 summary API로
// 경량 로드하고, 상세보기를 눌렀을 때만 `:id/detail`(평탄화 행)을 불러온다.
// Draft/Paid 목록을 상태별로 보고, Admin의 지급확정·Draft되돌리기·삭제를 처리한다.
// executedAt은 Paid면 실지급일, Draft면 지급예정일 — Draft에선 Admin이 수정 가능.
// totalAmount 음수(=상조 상계금)는 ▲로 표기한다(에러 아님).
// 마리(GiftAgent)는 서버가 viaMari 상조만 스코프(프론트 필터는 중복 방어)·읽기 전용.
// ─────────────────────────────────────────────

export interface CouponHistorySectionProps {
  isAdmin: boolean;
  refreshTrigger: number;
  /** GiftAgent(마리) 조회 뷰: viaMari 상조만 노출 + 쓰기 UI 숨김 */
  giftAgentView?: boolean;
  /** 라인 편집(제외/추가)으로 미정산이 바뀌었을 때 상위(미정산 섹션) 재조회 알림 */
  onLinesChanged?: () => void;
  /**
   * API role prefix 재정의 — 상조관리자 페이지가 BugoAgencyAdmin으로 지정(서버 스코프).
   * 생략 시 기존 규칙(마리=GiftAgent, 그 외 Admin).
   */
  role?: GiftCouponSettlementRole;
  /**
   * 상조명 칼럼 숨김 요청 (상조 view — 본인 상조뿐이라 상수 소음).
   * 실제로는 목록의 브랜드가 1종일 때만 숨긴다 — 복수 브랜드 예외 스코프면 구분 유지.
   */
  hideBrandColumn?: boolean;
}

const STATUS_FILTERS: { key: 'all' | GiftSettlementStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: GiftSettlementStatus.Draft, label: '정산집계' },
  { key: GiftSettlementStatus.Paid, label: '지급완료' },
];

export const CouponHistorySection: FC<CouponHistorySectionProps> = ({
  isAdmin,
  refreshTrigger,
  giftAgentView = false,
  onLinesChanged,
  role: roleProp,
  hideBrandColumn = false,
}) => {
  const toast = useToast();
  const role = roleProp ?? (giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin);

  const [{ loading, value }, fetchHistory] = useAsyncFn(
    () => getGiftCouponSettlementSummary({}, role),
    [role],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  const [statusFilter, setStatusFilter] = useState<'all' | GiftSettlementStatus>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // 상세보기 — 클릭 즉시 모달을 열고(shell = 목록 행 요약으로 헤더만),
  // detail이 도착하면 본문을 채운다 (버튼 로딩 대신 모달 내 로딩)
  const [detailShell, setDetailShell] = useState<GiftCouponSettlementSummaryItem | null>(
    null,
  );
  const [detailTarget, setDetailTarget] =
    useState<GiftCouponSettlementDetailResponse | null>(null);
  const [paidModal, setPaidModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    amount: number;
  }>({ open: false, id: '', name: '', amount: 0 });
  const [paidDate, setPaidDate] = useState(() => toYmd(new Date()));
  const [scheduleModal, setScheduleModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    date: string;
    hasDate: boolean;
  }>({ open: false, id: '', name: '', date: '', hasDate: false });

  // 라인 편집 (Admin·Draft 한정) — 제외 진행 중 쿠폰 id / 추가 피커 열림 / 추가 진행 중
  const [removingCouponId, setRemovingCouponId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  // 초과 제외분 강제 포함 진행 중 쿠폰 id (§5-2b)
  const [includingCouponId, setIncludingCouponId] = useState<string | null>(null);

  // 상세 조회 요청 시퀀스 — 늦게 도착한 응답이 다른 정산서의 모달을 덮어쓰는
  // 레이스 방지 (A 조회 중 닫고 B를 열면 A 응답은 버려져야 한다)
  const detailReqSeq = useRef(0);

  const closeDetail = () => {
    detailReqSeq.current += 1; // 진행 중 조회 무효화
    setDetailShell(null);
    setDetailTarget(null);
  };

  const openDetail = async (s: GiftCouponSettlementSummaryItem) => {
    const seq = ++detailReqSeq.current;
    setDetailShell(s); // 모달 즉시 오픈 — 본문은 detail 도착까지 스피너
    setDetailTarget(null);
    try {
      const detail = await getGiftCouponSettlementDetail(s._id, role);
      if (seq !== detailReqSeq.current) return; // 이미 닫혔거나 다른 정산서로 교체됨
      setDetailTarget(detail);
    } catch (e: any) {
      if (seq !== detailReqSeq.current) return;
      toast.show(`오류: ${e?.response?.data?.message || '상세 조회 실패'}`);
      setDetailShell(null); // 실패 시 모달 닫기 (로딩 화면에 갇히지 않게)
    }
  };

  /** 라인 편집 성공 후 공통 마무리 — 상세 모달 갱신 + 목록·미정산 재조회 */
  const refreshAfterLineEdit = async (id: string) => {
    const seq = ++detailReqSeq.current;
    try {
      const detail = await getGiftCouponSettlementDetail(id, role);
      if (seq === detailReqSeq.current) setDetailTarget(detail);
    } catch {
      if (seq === detailReqSeq.current) closeDetail(); // 재조회 실패 시 모달만 닫는다 (편집 자체는 반영됨)
    }
    fetchHistory();
    onLinesChanged?.();
  };

  /**
   * 정산서에서 쿠폰 제외 — 쿠폰 단위로 발송·환불 양쪽에서 뺀다.
   * (발송 후 환불된 쿠폰을 한쪽만 빼면 상쇄가 깨지는 집행 규칙과 동일 기준.
   * 해당 없는 쪽 id는 백엔드가 무시한다.) 제외분은 미정산으로 돌아간다.
   */
  const handleRemoveRow = async (row: CouponSettlementLineRow) => {
    if (!detailTarget) return;
    const bothSides =
      detailTarget.rows.filter((r) => r.giftCouponId === row.giftCouponId).length > 1;
    if (
      !window.confirm(
        `쿠폰 ${couponCodeLabel(row.fullCode)}을(를) 이 정산에서 제외할까요?\n` +
          (bothSides ? '발송·환불 라인이 함께 제외됩니다.\n' : '') +
          '제외된 쿠폰은 미정산으로 돌아가고, 정산금은 재계산됩니다.',
      )
    )
      return;
    setRemovingCouponId(row.giftCouponId);
    try {
      await patchGiftCouponSettlementLines(detailTarget.settlement._id, {
        removeSendCouponIds: [row.giftCouponId],
        removeRefundCouponIds: [row.giftCouponId],
      });
      toast.show('쿠폰을 제외했습니다. 미정산으로 돌아갑니다.');
      await refreshAfterLineEdit(detailTarget.settlement._id);
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '쿠폰 제외 실패'}`);
    } finally {
      setRemovingCouponId(null);
    }
  };

  /**
   * 추가 피커 확정 — 선택 쿠폰을 정산서에 추가 (반려분은 skipped 사유로 안내).
   * overQuotaIds는 초과 강제 포함분(§5-2b) — 백엔드 미반영이면 overQuota로 반려된다.
   */
  const handleAddSubmit = async (
    sendIds: string[],
    refundIds: string[],
    overQuotaIds: string[] = [],
  ) => {
    if (!detailTarget) return;
    setAddBusy(true);
    try {
      const result = await patchGiftCouponSettlementLines(detailTarget.settlement._id, {
        ...(sendIds.length ? { addSendCouponIds: sendIds } : {}),
        ...(refundIds.length ? { addRefundCouponIds: refundIds } : {}),
        ...(overQuotaIds.length ? { allowOverQuotaCouponIds: overQuotaIds } : {}),
      });
      const skippedNote = describeSkipped(result.skipped ?? []);
      // includedSend/Refund는 추가분이 아니라 재계산 후 정산서 전체 라인 수
      toast.show(
        `추가 반영 완료 — 정산서 발송 ${result.includedSend}건 · 환불 ${
          result.includedRefund
        }건${skippedNote ? ` · ${skippedNote}` : ''}`,
      );
      setAddOpen(false);
      await refreshAfterLineEdit(detailTarget.settlement._id);
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '쿠폰 추가 실패'}`);
    } finally {
      setAddBusy(false);
    }
  };

  /**
   * 집행 당시 초과 제외분(excludedRows)을 지급 대상으로 강제 편입 (§5-2b).
   * addSend + allowOverQuota를 함께 보내 쿼터 가드를 그 쿠폰만 면제한다.
   * 백엔드 미반영 구서버는 overQuota로 반려 → 안내만 하고 상태 변화 없음.
   */
  const handleIncludeExcluded = async (row: CouponSettlementLineRow) => {
    if (!detailTarget) return;
    if (
      !window.confirm(
        `구매수량 초과 발송인 쿠폰 ${couponCodeLabel(
          row.fullCode,
        )}을(를) 이 정산에 강제 포함할까요?\n` +
          `+${row.amount.toLocaleString(
            'ko-KR',
          )}원이 지급 대상이 되고, 낙전 차감 대상에서 빠집니다.`,
      )
    )
      return;
    setIncludingCouponId(row.giftCouponId);
    try {
      const result = await patchGiftCouponSettlementLines(detailTarget.settlement._id, {
        addSendCouponIds: [row.giftCouponId],
        allowOverQuotaCouponIds: [row.giftCouponId],
      });
      const rejected = (result.skipped ?? []).some(
        (s) => s.giftCouponId === row.giftCouponId && s.reason === 'overQuota',
      );
      if (rejected) {
        toast.show(
          '서버가 아직 초과 강제 포함(allowOverQuota)을 지원하지 않아 반려됐습니다 — 백엔드 반영 후 다시 시도해 주세요.',
        );
        return;
      }
      toast.show(
        `강제 포함 완료 — 정산서 발송 ${result.includedSend}건 · 환불 ${result.includedRefund}건`,
      );
      await refreshAfterLineEdit(detailTarget.settlement._id);
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '강제 포함 실패'}`);
    } finally {
      setIncludingCouponId(null);
    }
  };

  const handlePatchStatus = async (
    id: string,
    status: GiftSettlementStatus,
    label: string,
    executedAt?: string,
  ): Promise<boolean> => {
    setActionLoading(id);
    try {
      await patchGiftCouponSettlement(id, {
        status,
        ...(executedAt ? { executedAt } : {}),
      });
      toast.show(`${label} 완료`);
      fetchHistory();
      return true;
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || label + ' 실패'}`);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  /** Draft 지급예정일 저장/비우기 (executedAt 단독 패치, 상태 변경 없음) */
  const handleSaveSchedule = async (executedAt: string | null) => {
    setActionLoading(scheduleModal.id);
    try {
      await patchGiftCouponSettlement(scheduleModal.id, { executedAt });
      toast.show(executedAt ? '지급예정일 저장 완료' : '지급예정일 삭제 완료');
      setScheduleModal((m) => ({ ...m, open: false }));
      fetchHistory();
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '지급예정일 저장 실패'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (s: GiftCouponSettlementSummaryItem) => {
    if (
      !window.confirm(
        `${s.year}년 ${s.month}월 ${s.bugoBrandName} 쿠폰 정산을 삭제할까요?\n포함된 쿠폰은 다시 '미정산'으로 돌아갑니다.`,
      )
    )
      return;
    setActionLoading(s._id);
    try {
      await deleteGiftCouponSettlement(s._id);
      toast.show('삭제 완료');
      fetchHistory();
      onLinesChanged?.(); // 포함 쿠폰이 미정산으로 복귀 — 미정산 섹션도 재조회
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '삭제 실패'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const items = value?.items;

  // 정산집계(Draft) 일괄 재계산 — 반올림 규칙 개정(2026-07-24) 전에 동결된 Draft를
  // 현재 규칙(수수료 건별 올림·지급액 건별 내림)으로 재동결한다. 멱등이라 전체 Draft에
  // 돌려도 이미 새 규칙인 문서는 변화 없음 = "바뀐 것만" 갱신되는 효과. Paid는 제외.
  const [recomputeBusy, setRecomputeBusy] = useState(false);
  const handleRecomputeDrafts = async () => {
    const drafts = (items ?? []).filter(
      (s) => s.status === GiftSettlementStatus.Draft,
    );
    if (drafts.length === 0) {
      toast.show('재계산할 정산집계(Draft)가 없습니다.');
      return;
    }
    if (
      !window.confirm(
        `정산집계(Draft) ${drafts.length}건을 현재 반올림 규칙으로 재계산할까요?\n` +
          '개정 전에 집계된 정산서는 금액이 원 단위로 조정됩니다.\n' +
          '(지급완료된 정산은 건드리지 않습니다)',
      )
    )
      return;
    setRecomputeBusy(true);
    try {
      let changed = 0;
      let diffSum = 0;
      let skippedCount = 0;
      for (const s of drafts) {
        const result = await recomputeGiftCouponSettlement(s._id);
        if (!result) {
          toast.show(
            '서버가 아직 재계산(recomputeLines)을 지원하지 않습니다 — 백엔드 배포 후 다시 시도해 주세요.',
          );
          return;
        }
        const after = result.settlement.totalAmount;
        if (after !== s.totalAmount) {
          changed += 1;
          diffSum += after - s.totalAmount;
        }
        skippedCount += result.skipped?.length ?? 0;
      }
      const skippedNote =
        skippedCount > 0 ? ` · 재검증 반려 ${skippedCount}건(상세보기에서 확인)` : '';
      toast.show(
        changed > 0
          ? `재계산 완료 — Draft ${drafts.length}건 중 ${changed}건 금액 변경 (합계 ${
              diffSum > 0 ? '+' : ''
            }${diffSum.toLocaleString('ko-KR')}원)${skippedNote}`
          : `재계산 완료 — Draft ${drafts.length}건 모두 변경 없음${skippedNote}`,
      );
      fetchHistory();
      onLinesChanged?.();
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || '재계산 실패'}`);
      fetchHistory(); // 일부만 처리됐을 수 있어 목록을 서버 상태로 동기화
    } finally {
      setRecomputeBusy(false);
    }
  };

  const filtered = useMemo(() => {
    let all = items ?? [];
    // 마리(GiftAgent)는 viaMari 상조 정산만 — 서버 스코프의 중복 방어
    if (giftAgentView) {
      all = all.filter((s) => s.viaMari === true);
    }
    return statusFilter === 'all' ? all : all.filter((s) => s.status === statusFilter);
  }, [items, statusFilter, giftAgentView]);

  const summary = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((sum, s) => sum + s.totalAmount, 0),
    }),
    [filtered],
  );

  // 상조 view의 상조명 숨김 — 목록에 브랜드가 1종일 때만 (복수면 구분 위해 노출 유지)
  const hideBrand = useMemo(
    () => hideBrandColumn && new Set((items ?? []).map((s) => s.bugoBrandId)).size <= 1,
    [hideBrandColumn, items],
  );

  // 스피너는 첫 로드에서만 — 재조회(라인 편집·집행 후 refetch) 중에는 기존 목록을
  // 유지해서, 열려 있는 상세 모달·토스트가 언마운트(검색/탭/페이지 초기화)되지 않게 한다
  if (loading && !value)
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  if (!items?.length)
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );

  return (
    <div>
      {/* 상태 필터 + 요약 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                statusFilter === f.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <button
              onClick={handleRecomputeDrafts}
              disabled={recomputeBusy}
              title="개정(2026-07-24) 전에 집계된 정산집계(Draft)의 금액을 현재 반올림 규칙(수수료 건별 올림·지급액 건별 내림)으로 재계산합니다. 이미 새 규칙인 정산서는 변화가 없고, 지급완료 정산은 건드리지 않습니다."
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              {recomputeBusy ? '재계산 중...' : 'Draft 금액 재계산'}
            </button>
          )}
          <p className="text-sm text-gray-500">
            {summary.count}건 · 합계{' '}
            <span className={`font-semibold ${amountColorClass(summary.total)}`}>
              {formatSettlementAmount(summary.total)}
            </span>
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              {!hideBrand && <th className="px-3 py-2.5 text-left">상조</th>}
              <th className="px-3 py-2.5 text-left">정산월</th>
              <th className="px-3 py-2.5 text-left">상태</th>
              <th className="px-3 py-2.5 text-right">발송 건수</th>
              <th className="px-3 py-2.5 text-right">수익금(+)</th>
              <th className="px-3 py-2.5 text-right">환불 건수</th>
              <th className="px-3 py-2.5 text-right">환불금(−)</th>
              <th className="px-3 py-2.5 text-right">정산금액</th>
              <th className="px-3 py-2.5 text-left">지급(예정)일</th>
              <th className="px-3 py-2.5 text-left">메모</th>
              <th className="px-3 py-2.5 text-center">상세</th>
              {isAdmin && <th className="px-3 py-2.5 text-right">관리</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={(isAdmin ? 12 : 11) - (hideBrand ? 1 : 0)}
                  className="px-3 py-10 text-center text-gray-400"
                >
                  해당 상태의 정산이 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const isPaid = s.status === GiftSettlementStatus.Paid;
              const isActing = actionLoading === s._id;
              return (
                <tr key={s._id} className="hover:bg-gray-50">
                  {!hideBrand && (
                    <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                      {s.bugoBrandName}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(
                        s.status,
                      )}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  {/* 발송·환불 축 분해 (건수+금액 짝 배치 — 미정산 통계 테이블과 동일 구조).
                      신규 문서는 건별 정수 합이라 그대로, floorWon은 개정 전 문서의 소수 잔재 가드 */}
                  <td className="whitespace-nowrap px-3 py-3 text-right text-gray-600">
                    <span className="tabular-nums">{s.sendCount}건</span>
                    {/* 집행 당시 초과 자동 제외분 — 상세의 제외 섹션으로 유도 (가이드 §6-1, 모든 view 동일 표시) */}
                    {(s.overQuotaExcludedCount ?? 0) > 0 && (
                      <span className="block text-[11px] font-medium text-amber-600">
                        초과 제외 {s.overQuotaExcludedCount}건
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                    {s.sendAmount > 0 ? (
                      <span className="font-medium text-emerald-700">
                        +{floorWon(s.sendAmount).toLocaleString('ko-KR')}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.refundCount > 0 ? (
                      `${s.refundCount}건`
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                    {s.refundAmount > 0 ? (
                      <span className="font-medium text-rose-700">
                        −{s.refundAmount.toLocaleString('ko-KR')}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums ${amountColorClass(
                      s.totalAmount,
                    )}`}
                  >
                    {formatSettlementAmount(s.totalAmount)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {isPaid ? (
                      s.executedAt ? (
                        formatDate(s.executedAt)
                      ) : (
                        '-'
                      )
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {s.executedAt ? (
                          <>
                            <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                              예정
                            </span>
                            {formatDate(s.executedAt)}
                          </>
                        ) : (
                          '-'
                        )}
                        {isAdmin && (
                          <button
                            onClick={() =>
                              setScheduleModal({
                                open: true,
                                id: s._id,
                                name: s.bugoBrandName,
                                date: s.executedAt
                                  ? toYmd(new Date(s.executedAt))
                                  : toYmd(new Date()),
                                hasDate: !!s.executedAt,
                              })
                            }
                            className="rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500 hover:bg-gray-50"
                          >
                            수정
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-3 text-xs text-gray-500">
                    {s.memo || '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => openDetail(s)}
                      className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      상세보기
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {isPaid ? (
                          <button
                            disabled={isActing}
                            onClick={() =>
                              handlePatchStatus(
                                s._id,
                                GiftSettlementStatus.Draft,
                                'Draft 되돌리기',
                              )
                            }
                            className="rounded border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                          >
                            {isActing ? '처리 중...' : 'Draft로 되돌리기'}
                          </button>
                        ) : (
                          <>
                            <button
                              disabled={isActing}
                              onClick={() => {
                                // 예정일이 오늘 이전이면 실지급일로 프리필 (미래 예정일은 오늘로)
                                const today = toYmd(new Date());
                                const scheduled = s.executedAt
                                  ? toYmd(new Date(s.executedAt))
                                  : null;
                                setPaidDate(
                                  scheduled && scheduled <= today ? scheduled : today,
                                );
                                setPaidModal({
                                  open: true,
                                  id: s._id,
                                  name: s.bugoBrandName,
                                  amount: s.totalAmount,
                                });
                              }}
                              className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              지급확정
                            </button>
                            <button
                              disabled={isActing}
                              onClick={() => handleDelete(s)}
                              className="rounded border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                            >
                              삭제
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailShell && (
        <CouponHistoryDetailModal
          detail={detailTarget}
          fallbackSummary={detailShell}
          onClose={closeDetail}
          onExport={exportCouponSettlementDetailExcel}
          editable={
            isAdmin && detailTarget?.settlement.status === GiftSettlementStatus.Draft
          }
          onRemoveRow={handleRemoveRow}
          removingCouponId={removingCouponId}
          onAddCoupons={() => setAddOpen(true)}
          showAdditionalBadge
          showExcludedRows
          onIncludeExcludedRow={handleIncludeExcluded}
          includingCouponId={includingCouponId}
        />
      )}

      {/* 쿠폰 추가 피커 — 상세 모달 위에 뜸 (Draft·Admin 한정) */}
      {addOpen && detailTarget && (
        <CouponAddPendingModal
          settlement={detailTarget.settlement}
          busy={addBusy}
          onClose={() => setAddOpen(false)}
          onSubmit={handleAddSubmit}
        />
      )}

      {/* 지급예정일 수정 모달 (Draft 전용 — 미래 날짜 허용, 상태 변경 없음) */}
      {scheduleModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-base font-bold text-gray-800">지급예정일 수정</h3>
            <div className="mb-4 text-sm text-gray-600">
              <span className="font-medium">대상:</span> {scheduleModal.name}
            </div>
            <div className="mb-3 flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-700">지급예정일</span>
              <input
                type="date"
                value={scheduleModal.date}
                onChange={(e) =>
                  setScheduleModal((m) => ({ ...m, date: e.target.value }))
                }
                className="rounded border border-gray-300 px-2 py-1"
              />
            </div>
            <p className="mb-5 text-xs text-gray-400">
              지급확정 전까지는 예정일이며, 확정 시 입력한 지급일로 대체됩니다.
            </p>
            <div className="flex items-center justify-between gap-2">
              {scheduleModal.hasDate ? (
                <button
                  onClick={() => handleSaveSchedule(null)}
                  disabled={actionLoading === scheduleModal.id}
                  className="rounded-lg px-2 py-2 text-xs text-rose-500 hover:bg-rose-50 disabled:opacity-50"
                >
                  예정일 지우기
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setScheduleModal((m) => ({ ...m, open: false }))}
                  disabled={actionLoading === scheduleModal.id}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  취소
                </button>
                <button
                  onClick={() =>
                    handleSaveSchedule(
                      new Date(`${scheduleModal.date}T12:00:00`).toISOString(),
                    )
                  }
                  disabled={actionLoading === scheduleModal.id || !scheduleModal.date}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {actionLoading === scheduleModal.id ? '처리 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 지급확정 모달 (지급일 입력, 금액은 서버 계산값 그대로 — 음수 가능) */}
      {paidModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-base font-bold text-gray-800">지급완료 확정</h3>
            <div className="mb-4 space-y-2 text-sm text-gray-600">
              <div>
                <span className="font-medium">대상:</span> {paidModal.name}
              </div>
              <div>
                <span className="font-medium">{amountLabel(paidModal.amount)}:</span>{' '}
                <span className={`font-semibold ${amountColorClass(paidModal.amount)}`}>
                  {formatSettlementAmount(paidModal.amount)}
                </span>
              </div>
            </div>
            <div className="mb-3 flex items-center gap-2 text-sm">
              <span className="font-medium text-gray-700">지급일</span>
              <input
                type="date"
                value={paidDate}
                max={toYmd(new Date())}
                onChange={(e) => setPaidDate(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1"
              />
            </div>
            <p className="mb-5 text-xs text-gray-400">
              확정 후에는 Draft로 되돌리기 전까지 수정할 수 없습니다.
              <br />
              확정 시 서버가 현재 반올림 규칙으로 라인을 재계산해 동결합니다 — 개정 전에
              집계된 정산서는 표시 금액과 원 단위로 다를 수 있습니다(&lsquo;Draft 금액
              재계산&rsquo;으로 미리 맞출 수 있음).
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPaidModal((m) => ({ ...m, open: false }))}
                disabled={actionLoading === paidModal.id}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  const ok = await handlePatchStatus(
                    paidModal.id,
                    GiftSettlementStatus.Paid,
                    '지급완료 확정',
                    new Date(`${paidDate}T12:00:00`).toISOString(),
                  );
                  if (ok) setPaidModal((m) => ({ ...m, open: false }));
                }}
                disabled={actionLoading === paidModal.id}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading === paidModal.id ? '처리 중...' : '지급완료 확정'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast.message} durationMs={toast.durationMs} />
    </div>
  );
};
